import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, Globe2 } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTheme } from "../../theme/ThemeProvider";
import { SERVICES } from "./_shared";

const EDITORIAL_IMAGE =
  "https://images.pexels.com/photos/6279952/pexels-photo-6279952.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";

// Contemporary, empty law-firm boardroom — no people
const BLAKES_IMAGE =
  "https://images.pexels.com/photos/380768/pexels-photo-380768.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1920";

const EASE = [0.22, 1, 0.36, 1];

function GeometricComposition() {
  return (
    <svg
      viewBox="0 0 400 500"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    >
      <rect width="400" height="500" fill="#F9F8F6" />
      <rect x="30" y="30" width="260" height="320" fill="#4A6B5D" />
      <rect x="180" y="120" width="200" height="260" fill="#C89B3C" />
      <polygon points="40,300 360,260 340,480 60,470" fill="#B8573E" opacity="0.92" />
      <rect x="200" y="340" width="160" height="140" fill="#1E2F3E" />
      <circle cx="120" cy="160" r="74" fill="#F2EFE8" />
      <circle cx="120" cy="160" r="74" fill="none" stroke="#3D584C" strokeWidth="2" />
      <rect x="0" y="420" width="400" height="6" fill="#A87E27" />
      <rect x="300" y="60" width="40" height="40" fill="#1A2421" />
      <polygon points="260,200 340,240 260,280" fill="#F9F8F6" />
      <rect x="60" y="380" width="120" height="60" fill="#3D584C" />
    </svg>
  );
}

// Masked line-by-line reveal — each line slides up from behind an overflow mask
function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`block overflow-hidden pb-[0.06em] -mb-[0.06em] ${className}`}>
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

/* ---------- BLAKES VARIANT (sage + ochre) ---------- */
function BlakesHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  // Subtle parallax: backdrop drifts down + content softly fades on scroll
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      data-theme-variant="blakes"
      className="relative bg-sage-700 text-white overflow-hidden"
    >
      {/* Parallax backdrop with slow cinematic settle on load */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
        <motion.img
          src={BLAKES_IMAGE}
          alt=""
          aria-hidden="true"
          initial={{ scale: 1.16, opacity: 0 }}
          animate={{ scale: 1.05, opacity: 0.55 }}
          transition={{ duration: 1.8, ease: EASE }}
          className="w-full h-full object-cover"
        />
        {/* Sage (dark-light green) overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-sage-800 via-sage-700/85 to-sage-700/40" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-40 pb-28 lg:pt-52 lg:pb-40"
      >
        <div className="max-w-5xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            className="font-sans text-[11px] uppercase tracking-[0.28em] text-ochre-500"
          >
            A contemporary law practice
          </motion.p>

          {/* Ochre rule draws itself in */}
          <motion.span
            aria-hidden="true"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="block mt-6 h-px w-24 bg-ochre-500 origin-left"
          />

          <h1
            data-testid="hero-artwork"
            className="mt-8 font-serif text-[44px] sm:text-6xl lg:text-[92px] leading-[0.98] tracking-tight text-white"
          >
            <MaskedLine delay={0.45}>Built on trust.</MaskedLine>
            <MaskedLine delay={0.62} className="italic text-ochre-500">
              Driven by outcomes.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-2xl text-lg text-white/80 font-sans leading-relaxed"
          >
            Charis &amp; Mettle is a contemporary law practice advising
            founders, boards, investors and businesses on the deals and
            operational matters that shape companies.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <Link
              to="/intake"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center gap-3 bg-ochre-500 text-white hover:bg-ochre-600 px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
            >
              Begin matter intake
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1.5"
              />
            </Link>
            <a
              href="#practice"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center gap-2 text-white/90 hover:text-white font-sans text-[13px] tracking-wide transition-colors border-b border-white/30 hover:border-white pb-1"
            >
              Explore practice areas
              <ArrowDownRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1"
              />
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Slow editorial marquee on sage band */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.3 }}
        className="relative bg-sage-700 overflow-hidden border-t border-white/10 border-b border-sage-800"
      >
        <div data-testid="marquee-track" className="marquee-track flex whitespace-nowrap py-5 gap-16">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-16 shrink-0">
              {SERVICES.map((t, i) => (
                <span
                  key={`${k}-${i}`}
                  className="font-serif italic text-xl text-white flex items-center gap-16"
                >
                  {t}
                  <span className="text-ochre-500">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}

/* ---------- DEFAULT HERO (Bold / Editorial) ---------- */
function DefaultHero({ bold }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 lg:pt-44 lg:pb-32 overflow-hidden bg-cream-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="lg:col-span-7"
          >
            <h1 className="font-serif text-[44px] sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight text-charcoal">
              Built on trust.
              <span className="block italic text-sage-600">Driven by outcomes.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-charcoal-muted font-sans leading-relaxed">
              Charis &amp; Mettle is a contemporary law practice advising
              founders, boards, investors and businesses on the deals and
              operational matters that shape companies.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/intake"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-3 bg-sage-600 text-cream-50 hover:bg-sage-700 px-7 py-4 font-sans text-[12px] uppercase tracking-[0.22em] transition-colors duration-300"
              >
                Begin matter intake <ArrowRight size={16} />
              </Link>
              <a
                href="#practice"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center gap-2 text-charcoal hover:text-sage-700 font-sans text-[13px] tracking-wide transition-colors"
              >
                Explore practice areas
                <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
              </a>
            </div>
            <div
              data-testid="jurisdictions-band"
              className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-4 border-t border-cream-300 pt-8"
            >
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">
                <Globe2 size={14} className="text-sage-600" /> Jurisdictions we serve
              </span>
              <div className="flex items-center gap-6 font-serif text-lg text-charcoal">
                <span data-testid="jurisdiction-nigeria" className="relative">
                  Nigeria
                  <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-rust-500" />
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15, ease: EASE }}
            className="lg:col-span-5 relative"
          >
            <div data-testid="hero-artwork" className="relative aspect-[4/5] w-full overflow-hidden">
              {bold ? (
                <GeometricComposition />
              ) : (
                <>
                  <img src={EDITORIAL_IMAGE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-sage-900/30 via-transparent to-transparent" />
                </>
              )}
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div className="bg-cream-50/95 backdrop-blur-md px-5 py-3 border border-cream-300">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-charcoal-muted font-sans">Established</p>
                  <p className="font-serif text-2xl text-charcoal leading-tight">
                    Charis <span className="italic">&amp;</span> Mettle
                  </p>
                </div>
              </div>
            </div>
            {bold ? (
              <>
                <div className="hidden lg:block absolute -right-10 -top-10 w-28 h-28 bg-ochre-500 -z-10" />
                <div className="hidden lg:block absolute -left-8 -bottom-8 w-24 h-24 bg-rust-500 -z-10" />
              </>
            ) : (
              <div className="hidden lg:block absolute -left-10 -bottom-10 w-32 h-32 bg-sage-600/10 -z-10" />
            )}
          </motion.div>
        </div>
      </div>

      <div className="mt-24 lg:mt-32 overflow-hidden bg-sage-700">
        <div className="marquee-track flex whitespace-nowrap py-6 gap-16">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex items-center gap-16 shrink-0">
              {SERVICES.map((t, i) => (
                <span
                  key={`${k}-${i}`}
                  className="font-serif italic text-2xl text-white flex items-center gap-16"
                >
                  {t}
                  <span className="text-ochre-500">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Hero() {
  const { theme } = useTheme();
  if (theme === "blakes") return <BlakesHero />;
  return <DefaultHero bold={theme === "bold"} />;
}
